/**
 * SwapProvider - Abstract base class for swap aggregators
 *
 * This provides a unified interface for different swap aggregation services
 * like Jupiter, Titan Exchange, etc.
 */

export class SwapQuote {
    constructor({
        provider,
        inputMint,
        outputMint,
        inputAmount,
        outputAmount,
        priceImpact,
        slippageBps,
        route,
        transaction,
        expiresAt,
        computeUnits,
        platformFee,
        routeLabel,
        raw
    }) {
        this.provider = provider;           // Provider name (e.g., 'jupiter', 'titan')
        this.inputMint = inputMint;         // Input token mint address
        this.outputMint = outputMint;       // Output token mint address
        this.inputAmount = inputAmount;     // Raw input amount (unscaled)
        this.outputAmount = outputAmount;   // Raw output amount (unscaled)
        this.priceImpact = priceImpact;     // Price impact as decimal (0.01 = 1%)
        this.slippageBps = slippageBps;     // Slippage in basis points
        this.route = route;                 // Array of route steps
        this.transaction = transaction;     // Ready-to-sign transaction (if available)
        this.expiresAt = expiresAt;         // Expiration timestamp
        this.computeUnits = computeUnits;   // Estimated compute units
        this.platformFee = platformFee;     // Platform fee object { amount, bps }
        this.routeLabel = routeLabel;       // Human-readable route description
        this.raw = raw;                     // Raw provider response for debugging
    }

    /**
     * Get the exchange rate (output per input)
     */
    getRate(inputDecimals = 9, outputDecimals = 9) {
        const scaledInput = this.inputAmount / Math.pow(10, inputDecimals);
        const scaledOutput = this.outputAmount / Math.pow(10, outputDecimals);
        return scaledOutput / scaledInput;
    }

    /**
     * Get human-readable amounts
     */
    getHumanAmounts(inputDecimals = 9, outputDecimals = 9) {
        return {
            input: this.inputAmount / Math.pow(10, inputDecimals),
            output: this.outputAmount / Math.pow(10, outputDecimals)
        };
    }

    /**
     * Get price impact as percentage
     */
    getPriceImpactPercent() {
        return (this.priceImpact * 100).toFixed(2);
    }
}

export class SwapRouteStep {
    constructor({
        ammKey,
        label,
        inputMint,
        outputMint,
        inputAmount,
        outputAmount,
        feeAmount,
        feeMint
    }) {
        this.ammKey = ammKey;
        this.label = label;
        this.inputMint = inputMint;
        this.outputMint = outputMint;
        this.inputAmount = inputAmount;
        this.outputAmount = outputAmount;
        this.feeAmount = feeAmount;
        this.feeMint = feeMint;
    }
}

/**
 * Abstract SwapProvider class
 *
 * All swap providers must extend this class and implement the required methods.
 */
export class SwapProvider {
    constructor(config = {}) {
        if (new.target === SwapProvider) {
            throw new Error('SwapProvider is abstract and cannot be instantiated directly');
        }
        this.config = config;
        this.name = 'unknown';
        this.displayName = 'Unknown Provider';
        this.logo = null;
        this.isConnected = false;
        this.isStreaming = false;
        this._quoteCallbacks = [];
        this._errorCallbacks = [];
        this._statusCallbacks = [];
    }

    /**
     * Initialize the provider (connect to API, load resources, etc.)
     * @returns {Promise<boolean>} Whether initialization was successful
     */
    async initialize() {
        throw new Error('initialize() must be implemented by subclass');
    }

    /**
     * Disconnect/cleanup the provider
     */
    async disconnect() {
        throw new Error('disconnect() must be implemented by subclass');
    }

    /**
     * Get a single quote
     * @param {Object} params Quote parameters
     * @param {string} params.inputMint Input token mint
     * @param {string} params.outputMint Output token mint
     * @param {number} params.amount Amount in raw units
     * @param {number} params.slippageBps Slippage tolerance in basis points
     * @param {string} params.swapMode 'ExactIn' or 'ExactOut'
     * @param {string} params.userPublicKey User's wallet address
     * @returns {Promise<SwapQuote[]>} Array of quotes
     */
    async getQuote(params) {
        throw new Error('getQuote() must be implemented by subclass');
    }

    /**
     * Start streaming quotes (for providers that support it)
     * @param {Object} params Same as getQuote params
     * @returns {boolean} Whether streaming was started
     */
    startQuoteStream(params) {
        throw new Error('startQuoteStream() must be implemented by subclass');
    }

    /**
     * Stop streaming quotes
     */
    stopQuoteStream() {
        throw new Error('stopQuoteStream() must be implemented by subclass');
    }

    /**
     * Execute a swap using a quote
     * @param {SwapQuote} quote The quote to execute
     * @param {Object} wallet The wallet adapter instance
     * @returns {Promise<Object>} Transaction result
     */
    async executeSwap(quote, wallet) {
        throw new Error('executeSwap() must be implemented by subclass');
    }

    /**
     * Subscribe to quote updates (for streaming providers)
     * @param {Function} callback Callback receiving SwapQuote[]
     */
    onQuote(callback) {
        this._quoteCallbacks.push(callback);
    }

    /**
     * Subscribe to errors
     * @param {Function} callback Callback receiving Error
     */
    onError(callback) {
        this._errorCallbacks.push(callback);
    }

    /**
     * Subscribe to status changes
     * @param {Function} callback Callback receiving { status, message }
     */
    onStatus(callback) {
        this._statusCallbacks.push(callback);
    }

    /**
     * Emit quote update to subscribers
     */
    _emitQuote(quotes) {
        this._quoteCallbacks.forEach(cb => cb(quotes));
    }

    /**
     * Emit error to subscribers
     */
    _emitError(error) {
        this._errorCallbacks.forEach(cb => cb(error));
    }

    /**
     * Emit status change to subscribers
     */
    _emitStatus(status, message) {
        this._statusCallbacks.forEach(cb => cb({ status, message }));
    }

    /**
     * Clear all callbacks
     */
    clearCallbacks() {
        this._quoteCallbacks = [];
        this._errorCallbacks = [];
        this._statusCallbacks = [];
    }

    /**
     * Check if provider supports streaming
     */
    supportsStreaming() {
        return false;
    }

    /**
     * Get provider info
     */
    getInfo() {
        return {
            name: this.name,
            displayName: this.displayName,
            logo: this.logo,
            isConnected: this.isConnected,
            isStreaming: this.isStreaming,
            supportsStreaming: this.supportsStreaming()
        };
    }
}

export default SwapProvider;
